/**
 * callingHoursRule — shared builder for the constraint-layer PRERule that
 * activates a CallingHourWindow.
 *
 * A CallingHourWindow is inert on its own: it does nothing until a
 * `constraint`-layer rule with a `CHK_WITHIN_CALLING_HOURS` check references
 * its `windowId` (on a matching channel). This helper mints that rule's DTO so
 * both the one-step "Enforce these hours" toggle (CallingHoursForm) and the
 * one-click "Create enforcement rule" button (PolicyCallingHours) produce an
 * identical rule.
 *
 * The rule's effect is CONDITION so out-of-hours sends DEFER to the next
 * allowed window (via the delivery worker's calling-hours scheduler) rather
 * than being dropped.
 */
import type {
  CreateRuleDto,
  PREChannel,
  MessagePurpose,
} from '@respectly/shared-types';

/** The fields of a window needed to build its enforcement rule. */
export interface CallingHoursRuleSource {
  windowId: string;
  name: string;
  jurisdictionCode: string;
  channel: PREChannel | null;
  messagePurpose: MessagePurpose | null;
  sourceRef?: string | null;
  isActive?: boolean;
}

/** Mint a unique-ish rule id, e.g. `CUSTOM-US-FL-HRS-042`. */
function generateRuleId(jurisdictionCode: string): string {
  const num = String(Math.floor(Math.random() * 999) + 1).padStart(3, '0');
  return `CUSTOM-${jurisdictionCode}-HRS-${num}`;
}

/**
 * Build the CreateRuleDto for a calling-hours enforcement rule that points at
 * the given window. The rule is scoped to the same jurisdiction/channel/purpose
 * as the window so its channel matches (required for the window to fire).
 */
export function buildCallingHoursRuleDto(
  window: CallingHoursRuleSource,
): CreateRuleDto {
  return {
    ruleId: generateRuleId(window.jurisdictionCode),
    name: `${window.name} — Calling Hours`,
    description:
      'Restricts sends to the configured calling window; out-of-hours sends defer to the next allowed window.',
    jurisdictionCode: window.jurisdictionCode,
    layer: 'constraint',
    channel: window.channel,
    messagePurpose: window.messagePurpose,
    b2bOrB2c: null,
    requiredChecks: [
      {
        checkId: 'CHK_WITHIN_CALLING_HOURS',
        required: true,
        params: { window: window.windowId },
      },
    ],
    effect: 'CONDITION',
    remediation: 'Defer the send to the next allowed window.',
    sourceRef: window.sourceRef ?? null,
    userFacingSummary: null,
    userFacingCitation: null,
    priority: 50,
    isActive: window.isActive ?? true,
    isOverridable: false,
    isSystem: false,
  };
}

/**
 * Extract the set of windowIds that are referenced by a `CHK_WITHIN_CALLING_HOURS`
 * check across the given rules (active rules only). Used to flag inert windows.
 */
export function referencedWindowIdsFromRules(
  rules: Array<{
    isActive: boolean;
    requiredChecks: Array<{ checkId: string; params?: Record<string, unknown> | null }>;
  }>,
): string[] {
  const ids = new Set<string>();
  for (const rule of rules) {
    if (!rule.isActive) continue;
    for (const check of rule.requiredChecks) {
      if (check.checkId !== 'CHK_WITHIN_CALLING_HOURS') continue;
      const win = check.params?.window;
      if (typeof win === 'string' && win) ids.add(win);
    }
  }
  return [...ids];
}
